import { useState, useCallback } from "react";
import { Search, User, CreditCard, Shield, Mail, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

interface SearchResult {
  id: string;
  type: "user" | "subscription" | "ticket" | "email" | "audit";
  title: string;
  subtitle: string;
  meta?: string;
  path: string;
}

export function AdminGlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const { data: results, isLoading } = useQuery({
    queryKey: ["admin-global-search", query],
    queryFn: async (): Promise<SearchResult[]> => {
      if (query.length < 2) return [];

      const searchResults: SearchResult[] = [];

      // Search users
      const { data: users } = await supabase
        .from("profiles")
        .select("id, email, company_name, plan, created_at")
        .or(`email.ilike.%${query}%,company_name.ilike.%${query}%`)
        .limit(5);

      users?.forEach((user) => {
        searchResults.push({
          id: user.id,
          type: "user",
          title: user.email,
          subtitle: user.company_name || "No company",
          meta: user.plan,
          path: `/admins/users?search=${encodeURIComponent(user.email)}`,
        });
      });

      // Search subscriptions
      const { data: subscriptions } = await supabase
        .from("subscriptions")
        .select("id, plan, status, profiles!inner(email)")
        .or(`plan.ilike.%${query}%,status.ilike.%${query}%`)
        .limit(5);

      subscriptions?.forEach((sub) => {
        const profile = sub.profiles as unknown as { email: string };
        searchResults.push({
          id: sub.id,
          type: "subscription",
          title: `${sub.plan} - ${sub.status}`,
          subtitle: profile?.email || "Unknown user",
          path: `/admins/revenue`,
        });
      });

      // Search support tickets
      const { data: tickets } = await supabase
        .from("support_tickets")
        .select("id, subject, status, created_at")
        .ilike("subject", `%${query}%`)
        .limit(5);

      tickets?.forEach((ticket) => {
        searchResults.push({
          id: ticket.id,
          type: "ticket",
          title: ticket.subject,
          subtitle: ticket.status,
          meta: formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true }),
          path: `/admins/support`,
        });
      });

      return searchResults;
    },
    enabled: query.length >= 2,
    staleTime: 30000,
  });

  const handleSelect = useCallback(
    (result: SearchResult) => {
      setOpen(false);
      setQuery("");
      navigate(result.path);
    },
    [navigate]
  );

  const getIcon = (type: SearchResult["type"]) => {
    switch (type) {
      case "user":
        return User;
      case "subscription":
        return CreditCard;
      case "ticket":
        return FileText;
      case "email":
        return Mail;
      case "audit":
        return Shield;
    }
  };

  const getTypeColor = (type: SearchResult["type"]) => {
    switch (type) {
      case "user":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "subscription":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "ticket":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
      case "email":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      case "audit":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative flex items-center gap-2 w-full max-w-sm px-3 py-2 text-sm text-muted-foreground bg-muted/50 border rounded-md hover:bg-muted transition-colors"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search users, subscriptions...</span>
        <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          <span className="text-xs">⌘</span>S
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search users, subscriptions, tickets..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {query.length < 2 ? (
            <CommandEmpty>Type at least 2 characters to search...</CommandEmpty>
          ) : isLoading ? (
            <CommandEmpty>Searching...</CommandEmpty>
          ) : results?.length === 0 ? (
            <CommandEmpty>No results found.</CommandEmpty>
          ) : (
            <>
              {["user", "subscription", "ticket"].map((type) => {
                const typeResults = results?.filter((r) => r.type === type);
                if (!typeResults?.length) return null;

                const Icon = getIcon(type as SearchResult["type"]);
                return (
                  <CommandGroup
                    key={type}
                    heading={type.charAt(0).toUpperCase() + type.slice(1) + "s"}
                  >
                    {typeResults.map((result) => (
                      <CommandItem
                        key={result.id}
                        onSelect={() => handleSelect(result)}
                        className="flex items-center gap-3 py-3"
                      >
                        <div className={cn("p-1.5 rounded", getTypeColor(result.type))}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{result.title}</div>
                          <div className="text-sm text-muted-foreground truncate">
                            {result.subtitle}
                          </div>
                        </div>
                        {result.meta && (
                          <Badge variant="outline" className="shrink-0">
                            {result.meta}
                          </Badge>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
